import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { AtendentesService } from '../../../atendentes/Service';
import { minutesToTime, timeToMinutes } from '../../Enum';
import { useAppContext } from '../../../../contexts/AppContext';
import { useAgendaContext } from '../ContextAgenda';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { SettingsRow, SettingsSection } from '../../../configuracoes/estabelecimento/Form/SettingsGroup';

const atendentesService = new AtendentesService();

type Slot = { start: number; end: number };
type SlotType = 'horario' | 'closedSlots';

const isValidSlot = (slot: Slot) => slot.start >= 0 && slot.end > 0 && slot.start < slot.end;

// Configuracao valida so para o dia: sobrepoe a agenda semanal do atendente nesta data.
export const DateItemSettings = ({ date, onClose }: { date: string; onClose: () => void }) => {
    const [loading, setLoading] = useState(false);
    const { attendantId } = useFilterApp();
    const { settingsAgenda, loadSettings } = useAgendaContext();
    const { errorSnackbar, successSnackbar } = useAppContext();

    const [scheduleGap, setScheduleGap] = useState<number>(settingsAgenda?.scheduleGap || 10);
    const [minAdvanceTime, setMinAdvanceTime] = useState<number>(settingsAgenda?.minAdvanceTime || 0);
    const [horario, setHorario] = useState<Slot[]>(() => (settingsAgenda?.horario || []).map((s: Slot) => ({ ...s })));
    const [closedSlots, setClosedSlots] = useState<Slot[]>(() =>
        (settingsAgenda?.closedSlots || []).map((s: Slot) => ({ ...s }))
    );

    const setSlots = (type: SlotType, updater: (slots: Slot[]) => Slot[]) =>
        type === 'horario' ? setHorario(updater) : setClosedSlots(updater);

    const addSlot = (type: SlotType) => setSlots(type, (slots) => [...slots, { start: 0, end: 0 }]);

    const changeSlot = (type: SlotType, index: number, field: 'start' | 'end', value: string) =>
        setSlots(type, (slots) =>
            slots.map((slot, i) => (i === index ? { ...slot, [field]: value ? timeToMinutes(value) : 0 } : slot))
        );

    const removeSlot = (type: SlotType, index: number) =>
        setSlots(type, (slots) => slots.filter((_, i) => i !== index));

    const submit = async () => {
        if (!attendantId) {
            errorSnackbar('Selecione um atendente para configurar o dia.');
            return;
        }

        const filledHorario = horario.filter((slot) => slot.start || slot.end);
        const filledClosed = closedSlots.filter((slot) => slot.start || slot.end);
        if ([...filledHorario, ...filledClosed].some((slot) => !isValidSlot(slot))) {
            errorSnackbar('Confira os intervalos: o início precisa ser antes do fim.');
            return;
        }

        try {
            setLoading(true);
            await atendentesService.updateAgendaDate({
                atendenteId: attendantId,
                date,
                update: {
                    agenda: {
                        scheduleGap: Math.max(10, Number(scheduleGap) || 10),
                        minAdvanceTime: Math.max(0, Number(minAdvanceTime) || 0),
                        horario: filledHorario,
                        closedSlots: filledClosed,
                    },
                },
            });

            successSnackbar('Configuração do dia salva.');
            await loadSettings();
            onClose();
        } catch (e) {
            console.error('Erro ao salvar configuração do dia:', e);
            errorSnackbar('Não foi possível salvar a configuração do dia.');
        } finally {
            setLoading(false);
        }
    };

    const renderSlots = (type: SlotType, slots: Slot[], emptyText: string, addLabel: string) => (
        <>
            {!slots.length && (
                <SettingsRow>
                    <span className="text-sm text-base-content/55">{emptyText}</span>
                </SettingsRow>
            )}
            {slots.map((slot, index) => (
                <SettingsRow key={index} inline>
                    <div className="flex min-w-0 items-center gap-2">
                        <input
                            type="time"
                            aria-label="Início"
                            value={slot.start || slot.end ? minutesToTime(slot.start) : ''}
                            onChange={(e) => changeSlot(type, index, 'start', e.target.value)}
                            className="input estab-field w-28"
                            disabled={loading}
                        />
                        <span className="text-sm text-base-content/50">até</span>
                        <input
                            type="time"
                            aria-label="Fim"
                            value={slot.end ? minutesToTime(slot.end) : ''}
                            onChange={(e) => changeSlot(type, index, 'end', e.target.value)}
                            className="input estab-field w-28"
                            disabled={loading}
                        />
                    </div>
                    <button
                        type="button"
                        className="estab-link-button estab-link-button--destructive"
                        onClick={() => removeSlot(type, index)}
                        disabled={loading}
                        aria-label="Remover intervalo"
                        title="Remover intervalo"
                    >
                        <Trash2 aria-hidden="true" />
                    </button>
                </SettingsRow>
            ))}
            <SettingsRow>
                <button
                    type="button"
                    className="estab-link-button inline-flex items-center gap-1.5"
                    onClick={() => addSlot(type)}
                    disabled={loading}
                >
                    <Plus aria-hidden="true" />
                    {addLabel}
                </button>
            </SettingsRow>
        </>
    );

    return (
        <form
            className="estab-settings estab-settings--sheet space-y-6"
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
        >
            <SettingsSection footer="Vale apenas para este dia; os demais seguem a agenda semanal.">
                <SettingsRow label="Intervalo entre horários" htmlFor="day-schedule-gap" description="Em minutos">
                    <input
                        id="day-schedule-gap"
                        type="number"
                        inputMode="numeric"
                        min={10}
                        value={scheduleGap}
                        onChange={(e) => setScheduleGap(parseInt(e.target.value, 10) || 0)}
                        className="input estab-field w-full"
                        disabled={loading}
                    />
                </SettingsRow>
                <SettingsRow label="Antecedência mínima" htmlFor="day-min-advance" description="Em minutos">
                    <input
                        id="day-min-advance"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={minAdvanceTime}
                        onChange={(e) => setMinAdvanceTime(parseInt(e.target.value, 10) || 0)}
                        className="input estab-field w-full"
                        disabled={loading}
                    />
                </SettingsRow>
            </SettingsSection>

            <SettingsSection title="Horários de atendimento" footer="Sem intervalos, o dia fica fechado para agendamento.">
                {renderSlots('horario', horario, 'Nenhum horário de atendimento.', 'Adicionar intervalo')}
            </SettingsSection>

            <SettingsSection title="Horários fechados" footer="Bloqueiam a agenda dentro do horário de atendimento.">
                {renderSlots('closedSlots', closedSlots, 'Nenhum horário fechado.', 'Adicionar horário fechado')}
            </SettingsSection>

            <div className="estab-savebar gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[7rem]"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button type="submit" className="btn btn-primary flex-1 sm:flex-none sm:min-w-[9rem]" disabled={loading}>
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    Salvar
                </button>
            </div>
        </form>
    );
};
